import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Download,
  ArrowLeft,
  Building2,
  CreditCard,
  User,
  AlertCircle,
  FileText,
} from "lucide-react";
import * as ownerApi from "../../api/payroll/payrollReportApi";
import * as portalApi from "../../api/payroll/payrollReportPortalApi";
import {
  Button,
  Card,
  CardContent,
  Table,
  Badge,
  Alert,
  EmptyState,
  Skeleton,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

export default function BankTransferFile({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const { runId } = useParams();
  const base = isPortal ? "/staff-panel/payroll-runs" : "/payroll-runs";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const toast = useToast();

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getBankTransferFile(runId);
        setData(res?.data || res);
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load bank file");
      } finally {
        setLoading(false);
      }
    })();
  }, [runId, isPortal]);

  const downloadCsv = () => {
    if (!data?.transfers?.length) return;
    const headers = [
      "staffCode",
      "beneficiaryName",
      "accountNumber",
      "ifscCode",
      "bankName",
      "amount",
      "paymentReference",
    ];
    const lines = [headers.join(",")];
    data.transfers.forEach((t) => {
      lines.push(headers.map((h) => `"${t[h] ?? ""}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bank-transfer-${data.year}-${data.month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const transfers = data?.transfers || [];

  const columns = [
    {
      key: "staffCode",
      header: "Staff Code",
      render: (row) => (
        <Badge variant="secondary" className="font-mono text-[11px]">
          {row.staffCode || "—"}
        </Badge>
      ),
    },
    {
      key: "beneficiaryName",
      header: "Beneficiary",
      render: (row) => (
        <div className="flex items-center gap-2 font-medium text-neutral-900 dark:text-neutral-100">
          <User className="h-4 w-4 text-neutral-400 dark:text-neutral-500 shrink-0" />
          <span>{row.beneficiaryName || "—"}</span>
        </div>
      ),
    },
    {
      key: "accountNumber",
      header: "Account Number",
      render: (row) => (
        <div className="flex items-center gap-2 font-mono text-neutral-600 dark:text-neutral-300">
          <CreditCard className="h-4 w-4 text-neutral-400 dark:text-neutral-500 shrink-0" />
          <span>{row.accountNumber || "—"}</span>
        </div>
      ),
    },
    {
      key: "ifscCode",
      header: "IFSC Code",
      render: (row) => (
        <div className="flex items-center gap-2 font-mono uppercase text-neutral-600 dark:text-neutral-300">
          <Building2 className="h-4 w-4 text-neutral-400 dark:text-neutral-500 shrink-0" />
          <span>{row.ifscCode || "—"}</span>
        </div>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      render: (row) => (
        <span className="font-semibold text-success-700 tabular-nums">
          ₹{Number(row.amount || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      key: "paymentReference",
      header: "Reference",
      render: (row) => (
        <span className="font-mono text-body-sm text-neutral-500 dark:text-neutral-400">
          {row.paymentReference || "—"}
        </span>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
        <div className="space-y-2">
          <Skeleton height={20} width={120} />
          <Skeleton height={32} width={240} />
        </div>
        <Card>
          <CardContent className="p-6 space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex gap-4">
                <Skeleton height={20} width="15%" />
                <Skeleton height={20} width="25%" />
                <Skeleton height={20} width="20%" />
                <Skeleton height={20} width="20%" />
                <Skeleton height={20} width="20%" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-neutral-200 dark:border-neutral-800">
        <div>
          <Link
            to={`${base}/${runId}`}
            className="inline-flex items-center text-body-sm font-semibold text-neutral-500 dark:text-neutral-400 hover:text-primary-600 transition-colors mb-1"
          >
            <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to Run
          </Link>
          <h1 className="text-title-lg md:text-headline-lg font-display font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
            Bank Transfer File
          </h1>
          <p className="text-body-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
            {data?.month}/{data?.year} ·{" "}
            <span className="text-neutral-900 dark:text-neutral-100 font-semibold">{data?.count || 0}</span> transfers ·
            Total{" "}
            <span className="text-success-700 font-bold">
              ₹{Number(data?.totalAmount || 0).toLocaleString("en-IN")}
            </span>
          </p>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <Button
            type="button"
            onClick={downloadCsv}
            disabled={!transfers.length}
            variant="primary"
            leftIcon={<Download className="h-4 w-4" />}
          >
            Download CSV
          </Button>
        </div>
      </div>

      {/* Main Content Table Card */}
      <Card>
        <CardContent className="p-0">
          <Table
            columns={columns}
            data={transfers}
            emptyState={
              <EmptyState
                icon={FileText}
                title="No pending bank transfers available"
                description="Requires configured staff bank accounts + PENDING payslips on a PUBLISHED run."
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}